import { anyWindow } from "../_utils/global";
import { ICustomScrollStore } from "../_store/CustomScrollStoreModel";
import { setOffsetsOnScrollAction } from "../_features/setOffsetsOnScroll/setOffsetsOnScrollActions";
import { bindEvent } from "../_utils/domElementEventHelpers";
import { shouldBeConsumedByChild } from "../_shared/shouldBeConsumedByChild";

export const mouseWheelHandler = (
    element: HTMLElement,
    store: ICustomScrollStore
) =>
    bindEvent(
        element,
        getWheelKey(),
        mousewheelHandler.bind(null, element, store)
    );

function getWheelKey(): any {
    if (typeof window.onwheel !== "undefined") return "wheel";
    else if (typeof anyWindow.onmousewheel !== "undefined") return "mousewheel";
    else throw new Error("no wheel event");
}

function mousewheelHandler(
    element: HTMLElement,
    store: ICustomScrollStore,
    e: MouseEvent
) {
    const s = store.getState();
    if (!s.options) return;

    const { useBothWheelAxes, wheelSpeed, wheelPropagation } = s.options;
    const { scrollbarYActive, scrollbarXActive } = s.status;
    if (!scrollbarXActive && !scrollbarYActive) return;

    const [deltaX, deltaY] = getDeltaFromEvent(e);

    if (shouldBeConsumedByChild(element, e.target, deltaX, deltaY)) {
        return;
    }

    let shouldPrevent = false;
    const action = setOffsetsOnScrollAction(
        s.verticalDimension.scrollMeasure,
        s.horizontalDimension.scrollMeasure
    );

    if (!useBothWheelAxes) {
        // deltaX will only be used for horizontal scrolling and deltaY will
        // only be used for vertical scrolling - this is the default
        action.scrollTop -= deltaY * wheelSpeed;
        action.scrollLeft += deltaX * wheelSpeed;
    } else if (scrollbarYActive && !scrollbarXActive) {
        // only vertical scrollbar is active and useBothWheelAxes option is
        // active, so let's scroll vertical bar using both mouse wheel axes
        if (deltaY) {
            action.scrollTop -= deltaY * wheelSpeed;
        } else {
            action.scrollTop += deltaX * wheelSpeed;
        }
        shouldPrevent = true;
    } else if (scrollbarXActive && !scrollbarYActive) {
        // useBothWheelAxes and only horizontal bar is active, so use both
        // wheel axes for horizontal bar
        if (deltaX) {
            action.scrollLeft += deltaX * wheelSpeed;
        } else {
            action.scrollLeft -= deltaY * wheelSpeed;
        }
        shouldPrevent = true;
    }

    store.dispatch(action);

    shouldPrevent =
        shouldPrevent ||
        shouldPreventDefault(element, wheelPropagation, deltaX, deltaY);

    if (shouldPrevent && !e.ctrlKey) {
        e.stopPropagation();
        e.preventDefault();
    }
}

function shouldPreventDefault(
    element: HTMLElement,
    wheelPropagation,
    deltaX,
    deltaY
) {
    const roundedScrollTop = Math.floor(element.scrollTop);
    const isTop = element.scrollTop === 0;
    const isBottom =
        roundedScrollTop + element.offsetHeight === element.scrollHeight;
    const isLeft = element.scrollLeft === 0;
    const isRight =
        element.scrollLeft + element.offsetWidth === element.scrollWidth;

    let hitsBound;

    // pick axis with primary direction
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
        hitsBound = isTop || isBottom;
    } else {
        hitsBound = isLeft || isRight;
    }

    return hitsBound ? !wheelPropagation : true;
}

function getDeltaFromEvent(e) {
    let deltaX = e.deltaX;
    let deltaY = -1 * e.deltaY;

    if (typeof deltaX === "undefined" || typeof deltaY === "undefined") {
        // OS X Safari
        deltaX = (-1 * e.wheelDeltaX) / 6;
        deltaY = e.wheelDeltaY / 6;
    }

    if (e.deltaMode && e.deltaMode === 1) {
        // Firefox in deltaMode 1: Line scrolling
        deltaX *= 10;
        deltaY *= 10;
    }

    if (deltaX !== deltaX && deltaY !== deltaY /* NaN checks */) {
        // IE in some mouse drivers
        deltaX = 0;
        deltaY = e.wheelDelta;
    }

    if (e.shiftKey) {
        // reverse axis with shift key
        return [-deltaY, -deltaX];
    }
    return [deltaX, deltaY];
}
